// FILE: src/services/adminStatusService.js
//
// Client-side "is the signed-in user an admin?" check for UI affordances —
// currently the Admin item in HeaderUserMenu. This is NOT a security
// boundary: AdminGate re-verifies before rendering any admin surface, and
// every admin RPC re-checks the caller's JWT server-side via
// _caller_admin_email().
//
// Mechanism mirrors AdminGate's allow-list union:
//
//   1. The `list_admin_users` RPC. The RPC itself is admin-gated server-side
//      (it raises 42501 for non-admin callers), so a clean response is proof
//      of membership — no dedicated is_admin RPC needed.
//   2. REACT_APP_BOOTSTRAP_ADMINS / REACT_APP_ADMIN_EMAILS env vars as the
//      emergency fallback for when the DB is unreachable, matching
//      AdminGate's bootstrap behaviour.
//
// The result is cached per email for the lifetime of the page so reopening
// the menu doesn't re-fire the RPC. Sign-out/sign-in with a different
// account naturally misses the cache (keyed by email).

import { supabase } from './supabaseClient';

function getBootstrapAdmins() {
  return (
    (process.env.REACT_APP_BOOTSTRAP_ADMINS || '') + ',' +
    (process.env.REACT_APP_ADMIN_EMAILS    || '')
  )
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
}

let cache = { email: null, promise: null };

/**
 * Resolves true when the given email belongs to an admin.
 * Never rejects — any failure resolves to the bootstrap env-var fallback.
 *
 * @param {string} email
 * @returns {Promise<boolean>}
 */
export function checkAdminStatus(email) {
  const norm = (email || '').trim().toLowerCase();
  if (!norm) return Promise.resolve(false);
  if (cache.email === norm && cache.promise) return cache.promise;

  const promise = supabase
    .rpc('list_admin_users')
    .then(({ error }) => (error ? getBootstrapAdmins().includes(norm) : true))
    .catch(() => getBootstrapAdmins().includes(norm));

  cache = { email: norm, promise };
  return promise;
}

/** Test hook — clears the per-email cache. */
export function resetAdminStatusCache() {
  cache = { email: null, promise: null };
}
